import { useState } from 'react';
import { Check, Copy, Pencil } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import {
  Button, Drawer, DrawerContent, DrawerHeader, DrawerTitle,
  DrawerBody, DrawerFooter, IconButton, 
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { StatusPill } from '@vnetwork/ui/data';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDateTime, formatMoney } from '@/lib/format';
import { affiliateTone, humanize } from './status-tones';
import {
  useUpdateAffiliate,
  type Affiliate,
  type AffiliateTier,
  type AffiliateStatus,
} from '@/api/affiliate';
import { useAffiliatePolicyPermissions } from '../use-affiliate-policy-permissions';
import { NumericInput } from '@/components/numeric-input';

const TIER_OPTIONS: { value: AffiliateTier; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
];

const STATUS_OPTIONS: { value: AffiliateStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' },
];

interface EditAffiliateDrawerProps {
  affiliate: Affiliate;
  onClose: () => void;
}

function EditAffiliateDrawer({ affiliate, onClose }: EditAffiliateDrawerProps) {
  const toast = useToast();
  const update = useUpdateAffiliate(affiliate.affiliate_id);

  const [status, setStatus] = useState<AffiliateStatus>(affiliate.status);
  const [tier, setTier] = useState<AffiliateTier>(affiliate.tier);
  const [payoutThreshold, setPayoutThreshold] = useState(
    affiliate.payout_threshold != null ? String(affiliate.payout_threshold) : '',
  );
  const [commissionPercent, setCommissionPercent] = useState(
    affiliate.commission_percent != null ? String(affiliate.commission_percent) : '',
  );
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    try {
      await update.mutateAsync({
        status,
        tier,
        payout_threshold: payoutThreshold !== '' ? Number(payoutThreshold) : undefined,
        commission_percent: commissionPercent !== '' ? Number(commissionPercent) : undefined,
      });
      toast.show({ tone: 'success', title: 'Affiliate updated' });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update affiliate. Please try again.');
    }
  };

  return (
    <Drawer open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="right" size="sm">
        <DrawerHeader>
          <DrawerTitle>Edit Affiliate</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <form
            id="edit-affiliate-form"
            onSubmit={handleSubmit}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Status">
              {(fp) => (
                <Select value={status} onValueChange={(v) => setStatus(v as AffiliateStatus)}>
                  <SelectTrigger {...fp}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Tier">
              {(fp) => (
                <Select value={tier} onValueChange={(v) => setTier(v as AffiliateTier)}>
                  <SelectTrigger {...fp}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Payout Threshold (₫)"
              description="Minimum unpaid commission before a payout can be requested."
            >
              {(fp) => (
                <NumericInput
                  {...fp}
                  min={0}
                  value={payoutThreshold}
                  onChange={(e) => setPayoutThreshold(e.target.value)}
                  placeholder="500000"
                />
              )}
            </Field>

            <Field
              label="Fallback Commission %"
              description="Used only when no commission policy matches."
            >
              {(fp) => (
                <NumericInput
                  {...fp}
                  min={0}
                  max={100}
                  step={0.01}
                  value={commissionPercent}
                  onChange={(e) => setCommissionPercent(e.target.value)}
                  placeholder="e.g. 5"
                />
              )}
            </Field>

            {submitError && <Alert tone="danger" title={submitError} />}
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button form="edit-affiliate-form" type="submit" loading={update.isPending}>
            Save changes
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/** Copy-to-clipboard button for the referral link. */
function CopyLinkButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available — silently fail; the user can still select the text.
    }
  };

  return (
    <IconButton
      variant="ghost"
      size="sm"
      aria-label="Copy referral link"
      onClick={handleCopy}
      style={{ flexShrink: 0 }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </IconButton>
  );
}

interface OverviewTabProps {
  affiliate: Affiliate;
}

/**
 * Overview tab — basic-info fact grid (InfoGrid in a Card) + performance stat
 * band (Stat/StatGroup in a Card). Includes:
 * - Copy-to-clipboard on referral_link.
 * - Edit drawer (status/tier/payout_threshold/commission_percent) gated by canUpdateAffiliate.
 */
export function OverviewTab({ affiliate }: OverviewTabProps) {
  const perms = useAffiliatePolicyPermissions();
  const [editOpen, setEditOpen] = useState(false);

  const infoItems: InfoItem[] = [
    { label: 'Affiliate ID', value: affiliate.affiliate_id },
    { label: 'Name', value: affiliate.referrer_name || '—' },
    { label: 'Email', value: affiliate.referrer_email || '—' },
    { label: 'Phone', value: affiliate.referrer_phone || '—' },
    { label: 'Company', value: affiliate.referrer_company || '—' },
    {
      label: 'Referral Code',
      value: <code>{affiliate.referral_code}</code>,
    },
    {
      label: 'Referral Link',
      value: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <code style={{ fontSize: 'var(--vn-font-size-xs)', overflowWrap: 'anywhere', flex: 1 }}>
            {affiliate.referral_link}
          </code>
          <CopyLinkButton value={affiliate.referral_link} />
        </div>
      ),
      full: true,
    },
    {
      label: 'Tier',
      value: <StatusPill tone="neutral">{humanize(affiliate.tier)}</StatusPill>,
    },
    {
      label: 'Status',
      value: (
        <StatusPill tone={affiliateTone(affiliate.status)}>
          {humanize(affiliate.status)}
        </StatusPill>
      ),
    },
    {
      label: 'Fallback Commission Rate',
      value: `${affiliate.commission_percent ?? 0}%`,
    },
    { label: 'Payout Threshold', value: formatMoney(affiliate.payout_threshold) },
    { label: 'Payout Method', value: affiliate.payout_method || '—' },
    { label: 'Created', value: formatDateTime(affiliate.created_at) },
    { label: 'Updated', value: formatDateTime(affiliate.updated_at) },
  ];

  return (
    <>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card flat>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid items={infoItems} />
          </CardBody>
          {perms.canUpdateAffiliate && (
            <CardFooter>
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Pencil size={14} />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            </CardFooter>
          )}
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
          </CardHeader>
          <CardBody>
            <StatGroup columns={3}>
              <Stat label="Total Clicks" value={affiliate.total_clicks ?? 0} />
              <Stat label="Total Signups" value={affiliate.total_signups ?? 0} />
              <Stat label="Total Conversions" value={affiliate.total_conversions ?? 0} />
              <Stat label="Total Revenue" value={formatMoney(affiliate.total_revenue ?? 0)} />
              <Stat label="Total Commission" value={formatMoney(affiliate.total_commission ?? 0)} />
              <Stat label="Unpaid Commission" value={formatMoney(affiliate.unpaid_commission ?? 0)} />
            </StatGroup>
          </CardBody>
        </Card>
      </div>

      {editOpen && (
        <EditAffiliateDrawer
          affiliate={affiliate}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}
